import React from "react";
import { Badge, Checkbox, Modal, Table, Tag, Tooltip } from "antd";
import ThunderboltTwoTone from "@ant-design/icons/ThunderboltTwoTone";
import moment from "moment";
import { useDispatch } from "react-redux";
import { push } from "ducks/router";
import callGetUrgentRequestCount from "api/Request/callGetUrgentRequestCount";
import { callGetRequestList } from "api/Request/callGetRequestList";
import EmployeeInfoView from "components/UI/EmployeeInfoView";
import { urgentTaskTypes } from "components/UI/RequestTaskInput";

async function fetchUrgentCount() {
  try {
    const res = await callGetUrgentRequestCount();
    return (res && res.total) || 0;
  } catch (e) {
    return 0;
  }
}

function renderRemaining(deadline) {
  if (!deadline) return "";
  const minutes = moment(deadline, "YYYY-MM-DD HH:mm").diff(moment(), "minutes");
  if (minutes < 0) {
    return <span style={{ color: "#f5222d", fontWeight: 600 }}>Quá hạn</span>;
  }
  const text =
    minutes >= 60
      ? `${Math.floor(minutes / 60)}g ${minutes % 60}p`
      : `${minutes} phút`;
  const color = minutes < 60 ? "#f5222d" : minutes < 180 ? "#fa8c16" : undefined;
  return <span style={{ color, fontWeight: color ? 600 : undefined }}>{text}</span>;
}

// Mỗi dòng = 1 task gấp chưa hoàn thành (1 YC có thể có nhiều task gấp)
function toTaskRows(requests) {
  const rows = [];
  (requests || []).forEach((req) => {
    (req.urgent_tasks || [])
      .filter((task) => !task.is_done)
      .forEach((task, index) => {
        rows.push({ key: `${req.id}_${task.id || index}`, request: req, task });
      });
  });
  return rows.sort((a, b) =>
    (a.task.deadline || "").localeCompare(b.task.deadline || ""),
  );
}

function UrgentRequestNotification() {
  const dispatch = useDispatch();
  const [visible, setVisible] = React.useState(false);
  const [count, setCount] = React.useState(0);
  const [onlyMine, setOnlyMine] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState([]);

  React.useEffect(() => {
    function getCount() {
      fetchUrgentCount().then(setCount);
    }
    getCount();
    const interval = setInterval(getCount, 10000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    if (!visible) return;
    setLoading(true);
    callGetRequestList({ is_urgent: 1, urgent_mine: onlyMine ? 1 : undefined })
      .then((res) => setRows(toTaskRows(res && res.items)))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [visible, onlyMine]);

  function _onOpen() {
    setVisible(true);
    fetchUrgentCount().then(setCount);
  }

  function _onClose() {
    setVisible(false);
    fetchUrgentCount().then(setCount);
  }

  function _gotoRequest(id) {
    return (e) => {
      e.preventDefault();
      setVisible(false);
      dispatch(push(`/request/view/${id}`));
    };
  }

  function _gotoAll(e) {
    e.preventDefault();
    setVisible(false);
    dispatch(push("/request?is_urgent=1"));
  }

  const columns = [
    {
      title: "YC",
      key: "serial",
      render: (_, { request }) => (
        <a href={`/request/view/${request.id}`} onClick={_gotoRequest(request.id)}>
          {request.serial}
        </a>
      ),
    },
    {
      title: "Loại gấp",
      key: "task_type",
      render: (_, { task }) => {
        const type = urgentTaskTypes[task.task_type] || {};
        return (
          <Tooltip title={task.content}>
            <Tag color="volcano">
              {type.shortName} ×{type.weight}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Khách hàng",
      key: "customer",
      render: (_, { request }) =>
        request.customer_obj ? request.customer_obj.name : "",
    },
    {
      title: "NVKD chuyển",
      key: "employee",
      render: (_, { request }) =>
        request.employee_obj ? request.employee_obj.name : "",
    },
    {
      title: "NV xử lý",
      key: "user_id_b",
      render: (_, { task }) =>
        task.user_id_b ? (
          <EmployeeInfoView id={task.user_id_b} />
        ) : (
          <Tag color="red">Chưa giao</Tag>
        ),
    },
    {
      title: "Hạn",
      key: "deadline",
      render: (_, { task }) =>
        task.deadline
          ? moment(task.deadline, "YYYY-MM-DD HH:mm").format("DD/MM HH:mm")
          : "",
    },
    {
      title: "Còn lại",
      key: "remaining",
      render: (_, { task }) => renderRemaining(task.deadline),
    },
  ];

  return (
    <div className="notification d-inline-block mr-4">
      <Tooltip title="Yêu cầu gấp">
        <a href="javascript: void(0);" onClick={_onOpen}>
          <Badge overflowCount={999} count={count} style={styles.badge}>
            <ThunderboltTwoTone twoToneColor="#fa541c" style={styles.icon} />
          </Badge>
        </a>
      </Tooltip>
      <Modal
        title={`⚡ Yêu cầu gấp (${count})`}
        visible={visible}
        onCancel={_onClose}
        footer={
          <a href="/request?is_urgent=1" onClick={_gotoAll}>
            Xem tất cả trong Danh sách yêu cầu →
          </a>
        }
        width="90vw"
        style={{ maxWidth: 1200, top: 20 }}
        destroyOnClose
      >
        <div className="mb-2">
          <Checkbox checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)}>
            Chỉ việc của tôi
          </Checkbox>
        </div>
        <Table
          size="small"
          bordered
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={rows.length > 20 ? { pageSize: 20 } : false}
          locale={{ emptyText: "Không có yêu cầu gấp" }}
        />
      </Modal>
    </div>
  );
}

const styles = {
  icon: {
    fontSize: 25,
  },
  badge: {
    backgroundColor: "#fa541c",
  },
};

export default UrgentRequestNotification;
